/**
 * SmartTokenPicker — modal for inserting a Smart Block into a master spec.
 *
 * Two-column layout:
 *   left  : list of available smart blocks (built-ins + firm published)
 *           grouped by category, searchable
 *   right : details for the selected smart block — description, kind,
 *           param list, preview of "what it shows when compiled"
 *
 * On Insert, returns { smartBlockId, smartBlockKind, smartParams }; caller
 * creates the ContentBlock and inserts at the desired index.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { X, Search, Plus } from 'lucide-react';
import type { SmartBlock } from '../../../shared/types';
import { BUILT_IN_SMART_BLOCKS } from '../../../engine/smart-blocks';

interface Props {
  firmCatalog: SmartBlock[];
  onCancel: () => void;
  onInsert: (selection: {
    smartBlockId: string;
    smartBlockKind: string;
    smartParams: Record<string, any>;
  }) => void;
}

export function SmartTokenPicker({ firmCatalog, onCancel, onInsert }: Props) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string>(BUILT_IN_SMART_BLOCKS[0].id);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const merged: SmartBlock[] = useMemo(() => {
    const firmPublished = firmCatalog.filter((sb) => sb.status === 'published');
    return [...BUILT_IN_SMART_BLOCKS, ...firmPublished];
  }, [firmCatalog]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter(
      (sb) =>
        sb.name.toLowerCase().includes(q) ||
        (sb.description ?? '').toLowerCase().includes(q) ||
        (sb.category ?? '').toLowerCase().includes(q) ||
        sb.kind.toLowerCase().includes(q)
    );
  }, [merged, search]);

  const grouped = useMemo(() => {
    const m = new Map<string, SmartBlock[]>();
    for (const sb of filtered) {
      const cat = sb.category ?? 'Other';
      if (!m.has(cat)) m.set(cat, []);
      m.get(cat)!.push(sb);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const selected = filtered.find((sb) => sb.id === selectedId) ?? filtered[0];

  function handleInsert() {
    if (!selected) return;
    onInsert({
      smartBlockId: selected.id,
      smartBlockKind: selected.kind,
      smartParams: { ...(selected.defaultParams ?? {}) },
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Insert Smart Block"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          background: 'var(--sf, var(--color-surface))',
          color: 'var(--tx, var(--color-text))',
          borderRadius: 12,
          width: 'min(880px, 92vw)',
          height: 'min(620px, 86vh)',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--bd, var(--color-border))',
          boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderBottom: '1px solid var(--bd, var(--color-border))',
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: -0.1 }}>
              Insert Smart Block
            </div>
            <div
              style={{ fontSize: 11, color: 'var(--tm, var(--color-text-muted))', marginTop: 2 }}
            >
              Dynamic content that fills in from project context at compile time.
            </div>
          </div>
          <button
            onClick={onCancel}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 6,
              borderRadius: 6,
              color: 'var(--tm)',
              display: 'inline-flex',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body: two columns */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Left: list */}
          <div
            style={{
              width: 320,
              borderRight: '1px solid var(--bd, var(--color-border))',
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--sa, var(--color-surface-alt))',
            }}
          >
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--bd)' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  border: '1px solid var(--bd)',
                  borderRadius: 6,
                  background: 'var(--sf)',
                  padding: '6px 8px',
                }}
              >
                <Search size={13} color="var(--tm)" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search smart blocks…"
                  style={{
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    flex: 1,
                    fontSize: 12,
                    color: 'var(--tx)',
                  }}
                />
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px 12px' }}>
              {grouped.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--tm)', fontSize: 12 }}>
                  No matches.
                </div>
              )}
              {grouped.map(([cat, items]) => (
                <div key={cat} style={{ marginTop: 8 }}>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: 'var(--tm)',
                      padding: '4px 10px',
                    }}
                  >
                    {cat}
                  </div>
                  {items.map((sb) => {
                    const Icon =
                      // @ts-ignore lucide dynamic
                      (LucideIcons[sb.icon ?? 'Sparkles'] as any) ?? LucideIcons.Sparkles;
                    const isSel = sb.id === selected?.id;
                    return (
                      <button
                        key={sb.id}
                        onClick={() => setSelectedId(sb.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          textAlign: 'left',
                          padding: '7px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: isSel ? `${sb.color ?? '#228be6'}1a` : 'transparent',
                          cursor: 'pointer',
                          marginBottom: 2,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 22,
                            height: 22,
                            borderRadius: 5,
                            background: `${sb.color ?? '#228be6'}22`,
                            color: sb.color ?? 'var(--ac)',
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={13} strokeWidth={2.5} />
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--tx)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {sb.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Right: details */}
          <div
            style={{
              flex: 1,
              padding: '20px 24px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {selected ? (
              <SmartBlockDetails sb={selected} />
            ) : (
              <div style={{ color: 'var(--tm)' }}>Select a smart block to preview.</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            padding: '12px 20px',
            borderTop: '1px solid var(--bd, var(--color-border))',
            background: 'var(--sa)',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: '7px 14px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              color: 'var(--tx)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={!selected}
            style={{
              padding: '7px 14px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              background: 'var(--ac, var(--color-primary))',
              color: 'var(--color-text-inverse)',
              cursor: selected ? 'pointer' : 'not-allowed',
              opacity: selected ? 1 : 0.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Plus size={13} strokeWidth={2.5} />
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}

function SmartBlockDetails({ sb }: { sb: SmartBlock }) {
  // @ts-ignore lucide dynamic
  const Icon = (LucideIcons[sb.icon ?? 'Sparkles'] as any) ?? LucideIcons.Sparkles;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 8,
            background: `${sb.color ?? '#228be6'}22`,
            color: sb.color ?? 'var(--ac)',
            flexShrink: 0,
          }}
        >
          <Icon size={22} strokeWidth={2.2} />
        </span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx)' }}>{sb.name}</div>
          <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
            {sb.kind === 'custom' ? 'Custom' : 'Built-in'} · {sb.category ?? 'Other'}
          </div>
        </div>
      </div>

      {sb.description && (
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--tx)',
            margin: '4px 0 16px',
          }}
        >
          {sb.description}
        </p>
      )}

      {(sb.paramSchema?.length ?? 0) > 0 && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'var(--tm)',
              marginBottom: 6,
            }}
          >
            Configurable Parameters
          </div>
          <div
            style={{
              border: '1px solid var(--bd)',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--sa)',
            }}
          >
            {(sb.paramSchema ?? []).map((p, idx) => (
              <div
                key={p.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '8px 12px',
                  borderTop: idx > 0 ? '1px solid var(--bd)' : 'none',
                }}
              >
                <div style={{ minWidth: 130 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx)' }}>{p.label}</div>
                  <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 1 }}>{p.type}</div>
                </div>
                <div style={{ flex: 1, fontSize: 11, color: 'var(--tm)', paddingTop: 2 }}>
                  {p.description ?? p.placeholder ?? ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 'auto',
          padding: 12,
          background: 'var(--sa)',
          border: '1px dashed var(--bd)',
          borderRadius: 8,
          fontSize: 11,
          color: 'var(--tm)',
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: 'var(--tx)' }}>Note:</strong> This block fills in from project data.
        In the master spec editor you'll see only the declaration. The actual content appears when
        the spec is compiled into a project.
      </div>
    </>
  );
}
